import React from "react"
import { Helmet } from "react-helmet";
import { itemListSchema, WebSiteSchema, WebpageSchema, ArticleSchema, ProductSchema, BreadcrumbSchema, SiteNavigationSchema, FaqSchema, schemaLocalBusiness, schemaAuthor, schemaOrganization, schemaBrand, HowToOrderSchema, VideoObjectSchema, ShippingDetailSchema } from "../data/schema"
import ConfigContext from "../context/ConfigProvider";

const SEO = ({
    noIndex,
    locale,
    type,
    name,
    title,
    description,
    imgSrc,
    url,
    price,
    priceExVat,
    order_min,
    currency,
    availability,
    shippingCost,
    gender,
    brand,
    sku,
    mpn,
    gtin,
    material,
    pattern,
    color,
    ageGroup,
    size,
    category,
    itemsArray,

    lastReviewed,
    dateCreated,
    crumbArray,
    crumbIdArray,

    faqArray,

    webPageSchema,
    productSchema,
    breadcrumbSchema,
    siteNavigationSchema,
    localBusinessSchema,
    authorSchema,
    organizationSchema,
    brandSchema,
    howToOrderSchema
}) => {
    const { configData } = React.useContext(ConfigContext);

    const configedCrumbArray = React.useMemo(() => {
        let array = []
        if (!crumbArray && crumbIdArray?.length > 0) crumbIdArray?.forEach(id => {
            configData.categoryArray.forEach(obj => {
                obj.url = obj.url
                if (id === obj.bigcommerce_id) array = [obj, ...array]
            })
        })
        else if (crumbArray) array = crumbArray
        return array
    }, [])

    const seoTitle = () => {
        if (title?.length < 40) return title + " | Attitude.se — Experter på tryck sedan 2003"
        else return title
    }

    const seoDescription = () => {
        if (description?.length < 90) return description + " | Attitude.se — Vi trycker din design och logotyp på allt sedan 2003."
        else return description
    }

    return <Helmet>
        {noIndex ? <meta name="robots" content="none, noarchive, nositelinkssearchbox, nosnippet" /> : null}
        {/* Necessary */}
        <html lang="sv" />
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
        <meta http-equiv="Content-Security-Policy" content="upgrade-insecure-requests" />
        {/* Meta */}
        <title>{seoTitle()}</title>
        <meta name="description" content={seoDescription()} />
        <meta property="og:title" content={seoTitle()} />
        <meta property="og:description" content={seoDescription()} />
        <meta property="og:locale" content={locale ? locale : "sv_SE"} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={imgSrc ? imgSrc : "https://storage.googleapis.com/attitude-firestore.appspot.com/logo/attitude-og-image.png"} />
        <meta property="og:image:alt" content={title} />
        <meta property="og:site_name" content="Attitude.se" />
        <meta name="author" content="Attitude.se" />
        <link rel="canonical" href={url} />
        <link rel="alternate" href={url} hreflang="x-default" />
        {/* Business */}
        <meta property="og:type" content="business.business" />
        <meta property="business:contact_data:country_name" content="SE" />
        <meta property="business:contact_data:email" content="hej@attitude.se" />
        <meta property="business:contact_data:locality" content="Gothenburg" />
        <meta property="business:contact_data:phone_number" content="031-134141" />
        <meta property="business:contact_data:postal_code" content="41119" />
        <meta property="business:contact_data:street_address" content="Ingenjörsgatan 5" />
        <meta property="business:contact_data:website" content="https://www.attitude.se" />
        {/* Page Type Specific — Website */}
        {type === "website" ? <meta property="og:type" content="website" /> : null}
        {/* Page Type Specific — Category */}
        {type === "category" && itemsArray ? <script type="application/ld+json">{JSON.stringify(itemListSchema({ url: url, itemsArray: itemsArray }))}</script> : null}
        {/* Page Type Specific — Product */}
        {type === "product" ? <meta property="og:type" content="product" /> : null}
        {type === "product" ? <meta property="product:product_link" content={url} /> : null}
        {type === "product" ? <meta property="product:availability" content={availability ? availability : "instock"} /> : null}
        {type === "product" ? <meta property="product:condition" content="new" /> : null}
        {type === "product" ? <meta property="product:target_gender" content={gender ? gender : "unisex"} /> : null}
        {type === "product" ? <meta property="product:is_product_shareable" content="1" /> : null}
        {type === "product" ? <meta property="product:retailer" content="Attitude.se" /> : null}
        {type === "product" ? <meta property="product:retailer_title" content={title} /> : null}
        {type === "product" && category ? <meta property="product:retailer_category" content={category} /> : null}
        {type === "product" ? <meta property="product:price:amount" content={price} /> : null}
        {type === "product" ? <meta property="product:price:currency" content={currency} /> : null}
        {type === "product" ? <meta property="product:original_price:amount" content={price} /> : null}
        {type === "product" ? <meta property="product:original_price:currency" content={currency} /> : null}
        {type === "product" ? <meta property="product:pretax_price:amount" content={priceExVat} /> : null}
        {type === "product" ? <meta property="product:pretax_price:currency" content={currency} /> : null}
        {type === "product" ? <meta property="product:shipping_cost:amount" content={shippingCost ? shippingCost : "0"} /> : null}
        {type === "product" && brand ? <meta property="product:brand" content={brand} /> : null}
        {type === "product" && mpn ? <meta property="product:ean" content={mpn} /> : null}
        {type === "product" && gtin ? <meta property="product:gtin" content={gtin} /> : null}
        {type === "product" && material ? <meta property="product:material" content={material} /> : null}
        {type === "product" && pattern ? <meta property="product:pattern" content={pattern} /> : null}
        {type === "product" && color ? <meta property="product:color" content={color} /> : null}
        {type === "product" && ageGroup ? <meta property="product:age_group" content={ageGroup} /> : null}
        {type === "product" && size ? <meta property="product:size" content={size} /> : null}
        {type === "product" && category ? <meta property="product:category" content={category} /> : null}
        {/* Schema — JSON */}
        <script id="LocalBusinessSchema" type="application/ld+json">{JSON.stringify(schemaLocalBusiness)}</script>
        <script id="AuthorSchema" type="application/ld+json">{JSON.stringify(schemaAuthor)}</script>
        <script id="OrganizationSchema" type="application/ld+json">{JSON.stringify(schemaOrganization)}</script>
        <script id="BrandSchema" type="application/ld+json">{JSON.stringify(schemaBrand)}</script>
        <script id="VideoObjectSchema" type="application/ld+json">{JSON.stringify(VideoObjectSchema)}</script>
        <script id="ShippingDetailSchema" type="application/ld+json">{JSON.stringify(ShippingDetailSchema("pickup"))}</script>
        <script id="ShippingDetailSchema" type="application/ld+json">{JSON.stringify(ShippingDetailSchema("free"))}</script>
        <script id="ShippingDetailSchema" type="application/ld+json">{JSON.stringify(ShippingDetailSchema("standard"))}</script>
        <script id="ShippingDetailSchema" type="application/ld+json">{JSON.stringify(ShippingDetailSchema("express"))}</script>
        {/* Schema — Function */}
        <script id="ArticleSchema" type="application/ld+json">{JSON.stringify(ArticleSchema({ title, seoDescription, url, imgSrc, lastReviewed, dateCreated, locale }))}</script>
        <script id="WebSiteSchema" type="application/ld+json">{JSON.stringify(WebSiteSchema({ title, seoDescription, url, imgSrc, lastReviewed, dateCreated, locale }))}</script>
        <script id="WebPageSchema" type="application/ld+json">{JSON.stringify(WebpageSchema({ title, seoDescription, url, imgSrc, lastReviewed, dateCreated, locale }))}</script>
        {/* Conditation Schema — Function */}
        {productSchema ? <script id="ProductSchema" type="application/ld+json">{JSON.stringify(ProductSchema({ title, seoDescription, url, imgSrc, price, currency, sku, mpn, gtin }))}</script> : null}
        {breadcrumbSchema && configedCrumbArray ? <script id="BreadCrumbSchema" type="application/ld+json">{JSON.stringify(BreadcrumbSchema({ url: url, crumbArray: configedCrumbArray }))}</script> : null}
        {howToOrderSchema ? <script id="HowToSchema" type="application/ld+json">{JSON.stringify(HowToOrderSchema({ name, price, order_min, imgSrc, url }))}</script> : null}
        {siteNavigationSchema && configData.categoryArray ? <script id="SiteNavigationSchema" type="application/ld+json">{JSON.stringify(SiteNavigationSchema({ categoryArray: configData.categoryArray }))}</script> : null}
        {faqArray && faqArray?.length > 0 ? <script id="FaqSchema" type="application/ld+json">{JSON.stringify(FaqSchema({ faqArray: faqArray }))}</script> : null}
    </Helmet>
}

export default SEO